import { defineMessages } from "react-intl";

import { type AssessmentCopyVariants } from "@/modules/candidate/assessmentCopy";

export interface TimerError {
  message?: AssessmentCopyVariants;
  isTimeout?: boolean;
}

export const TIMER_ERROR_INITIAL_VALUE: TimerError = {};

const ERROR_CODE = {
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  BAD_REQUEST: 400,
  CONFLICT: 409,
} as const;

type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];

const messages = defineMessages({
  assessmentConflict: {
    id: "candidate.taking.assessment.timerError.conflict",
    defaultMessage:
      "You cannot have this assessment open in more than one tab. Please close all tabs to exit and try again.",
  },
  surveyConflict: {
    id: "candidate.taking.survey.timerError.conflict",
    defaultMessage:
      "You cannot have this survey open in more than one tab. Please close all tabs to exit and try again.",
  },
  assessmentAccessIssue: {
    id: "candidate.taking.assessment.timerError.accessIssue",
    defaultMessage:
      "There was an issue trying to access this assessment. Make sure you don't have this assessment open in multiple tabs. Please close all tabs to exit and try again.",
  },
  surveyAccessIssue: {
    id: "candidate.taking.survey.timerError.accessIssue",
    defaultMessage:
      "There was an issue trying to access this survey. Make sure you don't have this survey open in multiple tabs. Please close all tabs to exit and try again.",
  },
  assessmentGeneric: {
    id: "candidate.taking.assessment.timerError.generic",
    defaultMessage:
      "There was an issue with the timer. Make sure you don't have this assessment open in multiple tabs. Please close all tabs to exit and try again.",
  },
  surveyGeneric: {
    id: "candidate.taking.survey.timerError.generic",
    defaultMessage:
      "There was an issue with the timer. Make sure you don't have this survey open in multiple tabs. Please close all tabs to exit and try again.",
  },
});

const conflictMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentConflict,
  survey: messages.surveyConflict,
};

const accessIssueMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentAccessIssue,
  survey: messages.surveyAccessIssue,
};

const genericMessages: AssessmentCopyVariants = {
  assessment: messages.assessmentGeneric,
  survey: messages.surveyGeneric,
};

export function processTimerErrorCode(errorCode: ErrorCode): TimerError {
  switch (errorCode) {
    case ERROR_CODE.BAD_REQUEST:
      return { isTimeout: true };
    case ERROR_CODE.CONFLICT:
      return { message: conflictMessages };
    case ERROR_CODE.FORBIDDEN:
    case ERROR_CODE.NOT_FOUND:
      return { message: accessIssueMessages };
    default:
      return { message: genericMessages };
  }
}
